/**
 * @module hooks/useCanvasEngine
 * @summary Mount the engine onto the canvas + wrapper refs.
 * @description Mounts on first render, unmounts on cleanup. `engine.mount` is
 *   idempotent so React 18 StrictMode's dev double-invoke is safe.
 */
import { useEffect, type RefObject } from 'react';
import { engine } from '../engine/CanvasEngine';
import { useUiStore } from '../state/uiStore';

export function useCanvasEngine(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  wrapRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    engine.mount(canvas, wrap);
    // mount() synchronously runs registry.loadCustom(), registering any cached
    // custom symbols into DEFS. Bump the library now so the palette re-renders
    // with them — robust even if the engine's 'library' emit fired before
    // useEngineSync registered its listener.
    useUiStore.getState().bumpLibrary();
    return () => engine.unmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
